import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useMemo, useState } from 'react';
import { type FieldPath, useForm, useWatch } from 'react-hook-form';
import {
  type AgentFields,
  baseAgentFields,
} from '@/views/Referrals/views/NewReferral/types';
import {
  TrackContactModalEvent,
  useTrackContactModalAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactModal';
import { identityApi } from '$api-clients';
import type { IdentityRequest } from '$api-clients/hooks';

export enum ContactModalState {
  CLOSED,
  CONTACT_INCOMPLETE,
  ADDING_CONTACT,
  EDITING_CONTACT,
  EXISTING_CONTACT_FOUND,
}

type RestTeamMember = IdentityRequest<'/team-members'>['Data']['data'][number];

/**
 * Normalized team-member match used by the contact-form validation. The
 * `/team-members` list endpoint returns most fields inline, but the user's
 * primary email + address state come from `GET /users/{userId}` and the
 * brokerage details come from `GET /brokerages/{brokerageId}` (the list
 * endpoint only carries `team.brokerageId`). This shape pre-resolves those so
 * the validation logic reads flat, REST-derived values.
 */
interface MatchedTeamMember {
  roles: RestTeamMember['roles'];
  email: string;
  phoneNumber: string;
  license: { number: string; state: string };
  team: {
    id: string;
    legalName: string;
    brokerage: {
      name: string;
      licenseNumber: string;
      managingBroker: { firstName: string; lastName: string };
    } | null;
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    addressState: string;
  };
}

/**
 * Resolves the user's primary email + address state (not carried by the
 * `/team-members` list endpoint) via `GET /users/{userId}`.
 */
async function fetchUserDetails(userId: string) {
  const { data, error } = await identityApi.GET('/users/{userId}', {
    params: { path: { userId } },
  });

  if (error !== undefined) {
    datadogRum.addError(
      new Error(`Failed to fetch user details for user ${userId}`),
      { error },
    );
  }

  const primaryEmail =
    data?.contacts?.find(
      (contact) => contact.type === 'EMAIL' && contact.primary,
    )?.value ??
    data?.contacts?.find((contact) => contact.type === 'EMAIL')?.value;

  return {
    email: primaryEmail ?? '',
    addressState: data?.address?.state ?? '',
  };
}

/**
 * Resolves brokerage name/license/managing-broker (not carried by the
 * `/team-members` list endpoint) via `GET /brokerages/{brokerageId}`.
 */
async function fetchBrokerageDetails(brokerageId: string) {
  const { data, error } = await identityApi.GET('/brokerages/{brokerageId}', {
    params: { path: { brokerageId } },
  });

  if (error !== undefined) {
    datadogRum.addError(
      new Error(
        `Failed to fetch brokerage details for brokerage ${brokerageId}`,
      ),
      { error },
    );
  }

  if (!data) {
    return null;
  }

  return {
    name: data.name ?? '',
    licenseNumber: data.license?.number ?? '',
    managingBroker: {
      firstName: data.managingBroker?.firstName ?? '',
      lastName: data.managingBroker?.lastName ?? '',
    },
  };
}

async function buildMatchedTeamMember(
  member: RestTeamMember,
): Promise<MatchedTeamMember> {
  const brokerageId = member.team.brokerageId;

  const [userDetails, brokerage] = await Promise.all([
    fetchUserDetails(member.userId),
    brokerageId ? fetchBrokerageDetails(brokerageId) : Promise.resolve(null),
  ]);

  return {
    roles: member.roles,
    email: member.contact?.email ?? '',
    phoneNumber: member.contact?.phone ?? '',
    license: {
      number: member.license?.number ?? '',
      state: member.license?.state ?? '',
    },
    team: {
      id: member.team.id,
      legalName: member.team.legalName ?? '',
      brokerage,
    },
    user: {
      id: member.userId,
      firstName: member.firstName,
      lastName: member.lastName,
      email: userDetails.email,
      addressState: userDetails.addressState,
    },
  };
}

function useFindExistingTeamMember() {
  return useCallback(
    async ({
      licenseNumber,
      email,
    }: {
      licenseNumber: string;
      email: string;
    }): Promise<{
      member: MatchedTeamMember | undefined;
      matchingFields: string[];
    }> => {
      try {
        // Two requests (not one `q` OR query): we need to know which field
        // matched — license vs email — to drive the validation messaging below.
        const [licenseData, emailData] = await Promise.all([
          licenseNumber
            ? identityApi.GET('/team-members', {
                params: {
                  query: {
                    q: `license.number[eq]=${licenseNumber} & status[in]=[ACTIVE]`,
                    limit: 1,
                  },
                },
              })
            : Promise.resolve(undefined),
          email
            ? identityApi.GET('/team-members', {
                params: {
                  query: {
                    q: `email[eq]=${email} & status[in]=[ACTIVE]`,
                    limit: 1,
                  },
                },
              })
            : Promise.resolve(undefined),
        ]);

        const matchingLicenseUser = licenseData?.data?.data?.[0];
        const matchingEmailUser = emailData?.data?.data?.[0];
        const matchingFields = [];

        if (matchingLicenseUser) {
          matchingFields.push('agent license number');
        }
        if (matchingEmailUser) {
          matchingFields.push('email');
        }

        const matchingMember = matchingLicenseUser ?? matchingEmailUser;

        return {
          member: matchingMember
            ? await buildMatchedTeamMember(matchingMember)
            : undefined,
          matchingFields,
        };
      } catch (error) {
        datadogRum.addError(error);

        return {
          member: undefined,
          matchingFields: [],
        };
      }
    },
    [],
  );
}

function useFindMatchingBrokerage() {
  return useCallback(
    async ({
      licenseNumber,
      state,
    }: {
      licenseNumber: string;
      state: string;
    }) => {
      if (!licenseNumber) {
        return null;
      }

      try {
        const filters = [`licenseNumber[eq]=${licenseNumber}`];

        // Scope to the brokerage's market so a license number that collides
        // with a Side brokerage in another state isn't flagged as a match.
        if (state) {
          filters.push(`market[eq]=${state}`);
        }

        const { data } = await identityApi.GET('/brokerages', {
          params: {
            query: {
              q: filters.join(' & '),
            },
          },
        });

        return data?.data?.[0] ?? null;
      } catch (error) {
        datadogRum.addError(error);

        return null;
      }
    },
    [],
  );
}

interface UseContactFormModal {
  defaultValues: AgentFields;
  onSubmit: (values: AgentFields) => void;
  onClose?: (state: ContactModalState) => void;
  validateReferralAgent?: boolean;
}

export type IsFieldInvalidCallback = (key: FieldPath<AgentFields>) => boolean;

export function useContactFormModal({
  defaultValues,
  onSubmit,
  onClose,
  validateReferralAgent,
}: UseContactFormModal) {
  const trackEvent = useTrackContactModalAnalytics();

  const [error, setError] = useState('');
  const [state, setState] = useState<ContactModalState>(
    ContactModalState.CLOSED,
  );
  const [prevState, setPrevState] = useState(state);
  const [existingContact, setExistingContact] = useState<AgentFields>();
  const [validationContext, setValidationContext] = useState<{
    matchedFields: Partial<Record<FieldPath<AgentFields>, string>>;
    requiredToChange: 'all' | 'any';
  }>();

  const findExistingTeamMember = useFindExistingTeamMember();
  const findMatchingBrokerage = useFindMatchingBrokerage();

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const resetValidationState = () => {
    setError('');
    setValidationContext(undefined);
  };

  const handleClose = () => {
    onClose?.(state);
    setState(ContactModalState.CLOSED);
    resetValidationState();
  };

  const validate = async (data: AgentFields) => {
    // Check if the contact already exists
    const { member, matchingFields } = await findExistingTeamMember({
      licenseNumber: data.licenseNumber,
      email: data.email,
    });

    if (member) {
      setValidationContext({
        matchedFields: {
          licenseNumber: matchingFields.includes('agent license number')
            ? data.licenseNumber
            : undefined,
          email: matchingFields.includes('email') ? data.email : undefined,
        },
        requiredToChange: 'all',
      });

      const isReferralAgent = member.roles.some(
        (role) => role === 'REFERRAL_AGENT',
      );

      if (validateReferralAgent && isReferralAgent) {
        setError(
          `A Referral Agent cannot transact. In order to add this contact to your referral, update the ${matchingFields.join(' and ')}.`,
        );
        return false;
      }

      // Referral agents use the brokerage name for the company name, not the team name
      const companyName = isReferralAgent
        ? member.team.brokerage?.name
        : member.team.legalName;

      setExistingContact({
        ...baseAgentFields,
        isSideAgent: true,
        firstName: member.user.firstName ?? '',
        lastName: member.user.lastName ?? '',
        email: member.user.email || member.email || '',
        cellPhone: member.phoneNumber ?? '',
        state: member.user.addressState ?? '',
        licenseNumber: member.license.number ?? '',
        licenseState: member.license.state ?? '',
        userId: member.user.id || '',
        teamId: member.team.id || '',
        brokerage: {
          ...baseAgentFields.brokerage,
          licenseNumber: member.team.brokerage?.licenseNumber ?? '',
          companyName: companyName ?? '',
        },
        supervisingBroker: {
          ...baseAgentFields.supervisingBroker,
          firstName: member.team.brokerage?.managingBroker?.firstName ?? '',
          lastName: member.team.brokerage?.managingBroker?.lastName ?? '',
        },
      });

      setPrevState(state);
      setState(ContactModalState.EXISTING_CONTACT_FOUND);
      setError(
        `In order to add this contact to your referral, update the ${matchingFields.join(' and ')}`,
      );

      return false;
    }

    // Validate brokerage information
    const brokerage = await findMatchingBrokerage({
      licenseNumber: data.brokerage.licenseNumber,
      state: data.brokerage.state,
    });

    if (brokerage) {
      setValidationContext({
        matchedFields: {
          'brokerage.licenseNumber': data.brokerage.licenseNumber,
          licenseNumber: data.licenseNumber,
          email: data.email,
        },
        requiredToChange: 'any',
      });

      setError(
        "Based on our data, this contact does not exist at the brokerage you've entered. Update the brokerage license number or add a different email and agent license number to continue.",
      );

      return false;
    }

    return true;
  };

  const submit = async (values: AgentFields) => {
    if (await validate(values)) {
      onSubmit?.(values);
      setState(ContactModalState.CLOSED);
      resetValidationState();

      if (state === ContactModalState.ADDING_CONTACT) {
        trackEvent(TrackContactModalEvent.ClickSaveButton, {
          interactionContactId: values.id,
          contactType: values.userId ? 'side' : 'manual',
        });
      } else if (state === ContactModalState.EDITING_CONTACT) {
        trackEvent(TrackContactModalEvent.ClickUpdateButton, {
          interactionContactId: values.userId ?? values.id,
          contactType: 'manual',
        });
      }
    }
  };

  const handleBackToEditing = () => {
    setState(prevState);

    trackEvent(TrackContactModalEvent.ClickBackToEditingButton, {
      interactionContactId: existingContact?.userId,
    });
  };

  const handleUseExistingContact = () => {
    trackEvent(TrackContactModalEvent.ClickUseExistingContactButton, {
      interactionContactId: existingContact?.userId,
      contactType: 'side',
    });

    onSubmit?.(existingContact!);
    setState(ContactModalState.CLOSED);
    resetValidationState();
  };

  const openModal = ({
    state: modalState,
    data,
  }: {
    state: ContactModalState;
    data?: AgentFields;
  }) => {
    setState(modalState);

    if (data) {
      form.reset(data);
    }
  };

  // Custom validation for existing contact / brokerage error states
  const [brokerageLicenseNumber, licenseNumber, email] = useWatch({
    control: form.control,
    name: ['brokerage.licenseNumber', 'licenseNumber', 'email'],
  });

  const invalidFields = useMemo(() => {
    if (!validationContext) return [];

    const { matchedFields, requiredToChange } = validationContext;
    const fields = {
      'brokerage.licenseNumber': brokerageLicenseNumber,
      licenseNumber,
      email,
    };

    if (requiredToChange === 'all') {
      return Object.entries(matchedFields).reduce<string[]>(
        (agg, [key, value]) => {
          if (fields[key as keyof typeof fields] === value) {
            agg.push(key);
          }
          return agg;
        },
        [],
      );
    }

    const valid = Object.entries(matchedFields).some(([key, value]) => {
      return fields[key as keyof typeof fields] !== value;
    });

    return valid ? [] : Object.keys(matchedFields);
  }, [validationContext, brokerageLicenseNumber, licenseNumber, email]);

  const isValid =
    form.formState.isValid &&
    (state === ContactModalState.EXISTING_CONTACT_FOUND ||
      invalidFields.length === 0);

  // This must be a Proxy or subscriptions to formState won't work
  const formState = new Proxy(form.formState, {
    get(target, prop) {
      if (prop === 'isValid') {
        return isValid;
      }
      return target[prop as keyof typeof target];
    },
  });

  return {
    state,
    form: {
      ...form,
      formState,
    },
    error,
    existingContact,
    onSubmit:
      state === ContactModalState.EXISTING_CONTACT_FOUND
        ? handleUseExistingContact
        : submit,
    onCancel:
      state === ContactModalState.EXISTING_CONTACT_FOUND
        ? handleBackToEditing
        : handleClose,
    isFieldInvalid: (field: FieldPath<AgentFields>) =>
      invalidFields.includes(field),
    onClose: handleClose,
    openModal,
  };
}
